import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Share2, Plus } from 'lucide-react';
import { useT } from '../lib/i18n';
import { isCapacitorNative } from '../lib/platform';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

/** Pages client / publiques : ne pas pousser l'install de l'app entrepreneur. */
function isPublicClientPath(pathname) {
  return (
    pathname.startsWith('/sign/') ||
    pathname.startsWith('/team/join') ||
    pathname === '/terms' ||
    pathname === '/privacy' ||
    pathname === '/support' ||
    pathname === '/contact' ||
    pathname === '/help' ||
    pathname === '/delete-account'
  );
}

export function InstallPrompt() {
  const { lang } = useT();
  const location = useLocation();
  const hideForClient = isPublicClientPath(location.pathname || '');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('install-dismissed') === '1'
  );

  useEffect(() => {
    // App Store / Play Store native shell — never show PWA install banner.
    if (isCapacitorNative() || isStandalone() || dismissed || hideForClient) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: no beforeinstallprompt event, show manual instructions after 3s
    if (isIOS()) {
      const t = setTimeout(() => setShowIOS(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissed, hideForClient]);

  const dismiss = () => {
    localStorage.setItem('install-dismissed', '1');
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOS(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  if (isCapacitorNative() || dismissed || isStandalone() || hideForClient) return null;

  // Android / Chrome / Edge - native install prompt
  if (deferredPrompt) {
    return (
      <div
        data-testid="install-prompt-android"
        className="fixed bottom-24 left-4 right-4 z-50 max-w-md mx-auto card border-2 border-[#7A1F2B] p-4 flex items-center gap-3 shadow-2xl animate-in slide-in-from-bottom"
      >
        <div className="w-12 h-12 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
          <Download size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">
            {lang === 'fr' ? "Installer l'app" : 'Install the app'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {lang === 'fr' ? 'Plus rapide, hors-ligne, sur écran d\'accueil' : 'Faster, offline, on home screen'}
          </div>
        </div>
        <button
          data-testid="install-btn"
          onClick={install}
          className="h-11 px-4 rounded-lg bg-[#7A1F2B] text-white font-bold text-sm"
        >
          {lang === 'fr' ? 'Installer' : 'Install'}
        </button>
        <button
          data-testid="install-dismiss"
          onClick={dismiss}
          className="h-11 w-11 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  // iOS - manual instructions
  if (showIOS) {
    return (
      <div
        data-testid="install-prompt-ios"
        className="fixed bottom-24 left-4 right-4 z-50 max-w-md mx-auto card border-2 border-[#7A1F2B] p-4 shadow-2xl animate-in slide-in-from-bottom"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
            <Download size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm mb-1">
              {lang === 'fr' ? "Installer SoumissionPro sur iPhone" : 'Install SoumissionPro on iPhone'}
            </div>
            <div className="text-xs text-gray-600 leading-relaxed">
              {lang === 'fr' ? (
                <>Touchez <Share2 size={12} className="inline -mt-0.5" /> <b>Partager</b> en bas, puis <b>Sur l'écran d'accueil</b> <Plus size={12} className="inline -mt-0.5" /></>
              ) : (
                <>Tap <Share2 size={12} className="inline -mt-0.5" /> <b>Share</b> below, then <b>Add to Home Screen</b> <Plus size={12} className="inline -mt-0.5" /></>
              )}
            </div>
          </div>
          <button
            data-testid="install-dismiss"
            onClick={dismiss}
            className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
